// AuditLogModal — Chronologisches 90-Tage Audit-Log für Dienstplan-Änderungen
import React, { useState, useEffect } from 'react';
import { History, RefreshCw, FileEdit, Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { dienstApi } from '../../lib/api';
import { cn } from '../../lib/utils';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  formatAuditTimestamp,
  formatAuditGroupDate,
  getAuditGroupKey,
} from './auditLogFormatting';
const ACTION_META = {
  EINTEILUNG_CREATE:      { label: 'Erstellt',   icon: Plus,     color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  EINTEILUNG_UPDATE:      { label: 'Geändert',   icon: FileEdit, color: 'bg-blue-100 text-blue-800 border-blue-300' },
  EINTEILUNG_DELETE:      { label: 'Gelöscht',   icon: Trash2,   color: 'bg-red-100 text-red-800 border-red-300' },
  EINTEILUNG_BULK_CREATE: { label: 'Bulk-Setup', icon: Plus,     color: 'bg-purple-100 text-purple-800 border-purple-300' },
  SOLLSTAERKE_UPDATE:     { label: 'Sollstärke', icon: Users,    color: 'bg-amber-100 text-amber-800 border-amber-300' },
};

const groupByDate = (logs) => {
  const groups = {};
  logs.forEach(log => {
    const d = getAuditGroupKey(log.erstellt_am);
    (groups[d] ||= []).push(log);
  });
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
};

export function AuditLogModal({ open, onOpenChange, abteilungId }) {
  const [logs, setLogs] = useState([]);
  const [isLoading, setLoading] = useState(false);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { tage: 90, limit: 500 };
      if (abteilungId) params.abteilung_id = abteilungId;
      const res = await dienstApi.getDienstplanAuditLog(params);
      setLogs(res.data?.logs || []);
    } catch (error) {
      console.error('Load audit log error:', error);
      toast.error('Audit-Log konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadLogs();
  }, [open, abteilungId]);

  const grouped = groupByDate(logs);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col" data-testid="audit-log-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Dienstplan-Verlauf (letzte 90 Tage)
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between flex-wrap gap-2">
            <span>
              {isLoading ? 'Lade …' : `${logs.length} Einträge`}
              {abteilungId && ' (gefiltert auf Abteilung)'}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadLogs}
              disabled={isLoading}
              data-testid="audit-log-refresh"
            >
              <RefreshCw className={cn('w-4 h-4 mr-1', isLoading && 'animate-spin')} />
              Aktualisieren
            </Button>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 min-h-0" data-testid="audit-log-scroll">
          {isLoading ? (
            <div className="space-y-2 py-4">
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Keine Änderungen im Zeitraum</p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {grouped.map(([date, items]) => (
                <div key={date} data-testid={`audit-group-${date}`}>
                  <div className="sticky top-0 bg-background z-10 py-1.5 mb-1.5 border-b">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {formatAuditGroupDate(date)}
                    </h4>
                  </div>
                  <div className="space-y-1.5">
                    {items.map((log, i) => {
                      const meta = ACTION_META[log.aktion] || {
                        label: log.aktion, icon: FileEdit, color: 'bg-gray-100 text-gray-800 border-gray-300',
                      };
                      const Icon = meta.icon;
                      return (
                        <div
                          key={log.id || i}
                          className="flex items-start gap-2 p-2 rounded-md border hover:bg-muted/50 transition"
                          data-testid={`audit-log-row-${log.id || i}`}
                        >
                          <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className={cn('text-[10px] font-mono', meta.color)}>
                                {meta.label}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {formatAuditTimestamp(log.erstellt_am)}
                              </span>
                              {log.user_name && (
                                <span className="text-xs font-medium">{log.user_name}</span>
                              )}
                            </div>
                            {log.beschreibung && (
                              <p className="text-sm mt-1 whitespace-pre-wrap break-words" title={log.beschreibung}>
                                {log.beschreibung}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AuditLogModal;
